import type { Feature, FeatureCollection, Polygon } from 'geojson';

/**
 * H3 cell index (string format)
 * @example "88a8a0651ffffff"
 */
export type H3Index = string;

/**
 * Bounding box
 */
export interface BoundingBox {
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
}

/**
 * Coordenadas geográficas
 */
export interface LatLng {
  lat: number;
  lng: number;
}

/**
 * Feature GeoJSON de celda H3
 */
export interface H3CellFeature extends Feature<Polygon> {
  id: H3Index;
  properties: {
    h3Index: H3Index;
    resolution: number;
    /** Código Col-Fila legible (ej. "A-01"). Presente si la malla tiene cellCodes generados. */
    cellCode?: string;
  };
}

/**
 * FeatureCollection de malla H3
 */
export interface H3GridFeatureCollection extends FeatureCollection<Polygon> {
  features: H3CellFeature[];
  metadata?: {
    resolution: number;
    cellCount: number;
    bounds: BoundingBox;
  };
}

/**
 * Configuración de estilos visuales de una malla H3.
 */
export interface GridStyle {
  fillColor: string;
  fillOpacity: number;
  lineColor: string;
  lineWidth: number;
  hoverColor: string;
  hoverOpacity: number;
  selectedColor: string;
  selectedOpacity: number;
  showLabels: boolean;
  labelsMinZoom: number;
  /** Tamaño de fuente de las etiquetas en px */
  labelsSize: number;
}

/** Valores por defecto cuando la malla no tiene estilos guardados. */
export const DEFAULT_GRID_STYLE: GridStyle = {
  fillColor: '#22D3EE',
  fillOpacity: 0.02,  // Casi transparente por defecto - solo borde visible
  lineColor: '#22D3EE',
  lineWidth: 1,
  hoverColor: '#22D3EE',
  hoverOpacity: 0.35,
  selectedColor: '#06B6D4',
  selectedOpacity: 0.5,
  showLabels: false,
  labelsMinZoom: 11,
  labelsSize: 10,
};

/** Opacidad de relleno cuando la malla está activa e interactiva */
export const ACTIVE_GRID_FILL_OPACITY = 0.15;

/** Resuelve el estilo efectivo de una malla, aplicando defaults para campos faltantes. */
export function getEffectiveStyle(grid: SpatialGrid): GridStyle {
  return {
    fillColor:       grid.style?.fillColor       ?? DEFAULT_GRID_STYLE.fillColor,
    fillOpacity:     grid.style?.fillOpacity      ?? DEFAULT_GRID_STYLE.fillOpacity,
    lineColor:       grid.style?.lineColor        ?? DEFAULT_GRID_STYLE.lineColor,
    lineWidth:       grid.style?.lineWidth        ?? DEFAULT_GRID_STYLE.lineWidth,
    hoverColor:      grid.style?.hoverColor       ?? DEFAULT_GRID_STYLE.hoverColor,
    hoverOpacity:    grid.style?.hoverOpacity     ?? DEFAULT_GRID_STYLE.hoverOpacity,
    selectedColor:   grid.style?.selectedColor    ?? DEFAULT_GRID_STYLE.selectedColor,
    selectedOpacity: grid.style?.selectedOpacity  ?? DEFAULT_GRID_STYLE.selectedOpacity,
    showLabels:      grid.style?.showLabels       ?? DEFAULT_GRID_STYLE.showLabels,
    labelsMinZoom:   grid.style?.labelsMinZoom    ?? DEFAULT_GRID_STYLE.labelsMinZoom,
    labelsSize:      grid.style?.labelsSize       ?? DEFAULT_GRID_STYLE.labelsSize,
  };
}

/**
 * Malla H3 almacenada en BD (respuesta del backend)
 */
export interface SpatialGrid {
  id: string;
  projectId: string;
  name: string;
  resolution: number;
  cells: H3Index[];
  /**
   * Mapa { h3Index → código Col-Fila } generado al crear la malla.
   * Es null en mallas creadas antes de la migración AddCellCodesToSpatialGrids.
   * @example { "8928308280fffff": "A-01", "8928308281fffff": "A-02" }
   */
  cellCodes: Record<string, string> | null;
  /** Estilos visuales persistidos. Null en mallas creadas antes de v3.1. */
  style: GridStyle | null;
  /** Nombres personalizados por celda: { h3Index → nombre }. Null si no hay ninguno. */
  cellNames: Record<string, string> | null;
  metadata: {
    cellCount: number;
    bounds: BoundingBox;
    createdBy?: string;
    description?: string;
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * DTO para crear malla H3
 */
export interface CreateGridDto {
  name: string;
  resolution: number;
  description?: string;
}

// ==================== CANONICAL STREET NETWORK TYPES ====================

/**
 * GeoJSON Point geometry
 */
export interface PointGeometry {
  type: 'Point';
  coordinates: [number, number]; // [lng, lat]
}

/**
 * GeoJSON LineString geometry
 */
export interface LineStringGeometry {
  type: 'LineString';
  coordinates: Array<[number, number]>; // Array of [lng, lat]
}

/**
 * Source type for nodes and edges
 */
export type SourceType = 'osm' | 'manual' | 'municipal' | 'import';

/**
 * Intersection type classification
 */
export type IntersectionType = 'simple' | 'roundabout' | 'grade_separated';

/**
 * Road classification
 */
export type RoadClass =
  | 'motorway'
  | 'trunk'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'residential'
  | 'service'
  | 'unclassified'
  | 'pedestrian'
  | 'cycleway'
  | 'footway';

/**
 * Access modes for an edge
 */
export type AccessMode = 'vehicle' | 'bicycle' | 'pedestrian';

/**
 * Oneway direction (relative to geometry)
 */
export type OnewayDirection = 'forward' | 'backward';

/**
 * Surface type
 */
export type SurfaceType =
  | 'paved'
  | 'unpaved'
  | 'gravel'
  | 'dirt'
  | 'cobblestone'
  | 'asphalt'
  | 'concrete';

/**
 * Street morphology form (B - Morfología Urbana)
 */
export type StreetForm = 'street' | 'avenue' | 'boulevard' | 'alley' | 'passage' | 'road';

/**
 * Exclusive mode restriction (C - Modo y Accesibilidad)
 */
export type ExclusiveMode = 'none' | 'pedestrian_only' | 'bike_only' | 'transit_only';

/**
 * Access restriction type (C - Modo y Accesibilidad)
 */
export type AccessRestriction = 'none' | 'private' | 'restricted' | 'delivery_only' | 'emergency_only';

/**
 * Parking configuration (D - Sentido y Configuración)
 */
export type ParkingConfig = 'none' | 'both' | 'right' | 'left';

/**
 * Road surface condition (E - Superficie y Estado)
 */
export type ConditionType = 'good' | 'fair' | 'poor' | 'under_construction' | 'unknown';

/**
 * Road smoothness - OSM compatible (E - Superficie y Estado)
 */
export type SmoothnessType = 'excellent' | 'good' | 'intermediate' | 'bad' | 'very_bad' | 'impassable';

/**
 * Node/Edge status
 */
export type NetworkStatus = 'active' | 'deprecated';

/**
 * Estado semántico de denominación de un segmento.
 */
export type EdgeNamingStatus =
  | 'named'
  | 'partial'
  | 'unnamed'
  | 'inferred'
  | 'ambiguous'
  | 'missing_intersections'
  | 'unresolved';

/**
 * BranchIntent - Intención explícita de creación de rama (FIX: Hallazgo #5)
 *
 * En vez de inferir la acción desde state.snapTarget stale, cada click
 * envía una intención explícita con ID y coordenadas del elemento clickeado.
 */
export type BranchIntent =
  | { type: 'free-point'; coords: [number, number] }
  | { type: 'snap-node'; nodeId: string; coords: [number, number] }
  | { type: 'snap-edge'; edgeId: string; coords: [number, number] };

/**
 * CanonicalNode - Represents an intersection in the street network
 */
export interface CanonicalNode {
  id: string;
  projectId: string;
  geometry: PointGeometry;
  nodeVersion: number;
  layer: number;
  intersectionType: IntersectionType | null;
  sourceType: SourceType;
  sourceReference: string | null;
  isManualAuthority: boolean;
  status: NetworkStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * CanonicalEdge - Represents a street segment in the network
 */
export interface CanonicalEdge {
  id: string;
  projectId: string;
  fromNode: CanonicalNode;
  toNode: CanonicalNode;
  geometry: LineStringGeometry;
  lengthMeters: number | null;
  edgeVersion: number;
  parentEdgeId: string | null;
  roadClass: RoadClass | null;
  accessMode: AccessMode[] | null;
  oneway: boolean;
  onewayDirection: OnewayDirection | null;
  layer: number;
  speedLimitKmh: number | null;
  typicalSpeedKmh: number | null;
  surfaceType: SurfaceType | null;
  lanes: number | null;
  widthMeters: number | null;
  // Campos multinivel (Plan Red Urbana UX - Pasos 8-9)
  streetForm: StreetForm | null;           // B - Morfología Urbana
  exclusiveMode: ExclusiveMode | null;     // C - Modo y Accesibilidad
  accessRestriction: AccessRestriction | null; // C - Modo y Accesibilidad
  parking: ParkingConfig | null;           // D - Sentido y Configuración
  condition: ConditionType | null;         // E - Superficie y Estado
  smoothness: SmoothnessType | null;       // E - Superficie y Estado
  maxHeightM: number | null;               // G - Restricciones Físicas
  maxWeightT: number | null;               // G - Restricciones Físicas
  maxWidthM: number | null;                // G - Restricciones Físicas
  // Campos base
  sourceType: SourceType;
  sourceReference: string | null;
  isManualAuthority: boolean;
  status: NetworkStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * Semántica persistida de un segmento canónico.
 */
export interface EdgeSemanticInfo {
  edgeId: string;
  projectId: string;
  displayName: string | null;
  uniqueDisplayName: string | null;
  semanticCode: string | null;
  userDisplayCode: string | null;
  h3CellR9: string | null;
  sectorCode: string | null;
  localSequence: number | null;
  streetNameResolved: string | null;
  namingStatus: EdgeNamingStatus;
  confidenceScore: number;
  isUserNamed: boolean;
  fromStreetCandidates: string[];
  toStreetCandidates: string[];
  baseStreetName: string | null;
  baseRoadRef: string | null;
  roadType: string | null;
  resolutionMethod: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * DTO for creating a canonical node
 */
export interface CreateCanonicalNodeDto {
  geometry: PointGeometry;
  sourceType?: SourceType;
  sourceReference?: string;
  layer?: number;
  intersectionType?: IntersectionType;
  isManualAuthority?: boolean;
}

/**
 * DTO for creating a canonical edge
 */
export interface CreateCanonicalEdgeDto {
  fromNodeId: string;
  toNodeId: string;
  geometry: LineStringGeometry;
  lengthMeters?: number;
  sourceType?: SourceType;
  sourceReference?: string;
  roadClass?: RoadClass;
  accessMode?: AccessMode[];
  oneway?: boolean;
  onewayDirection?: OnewayDirection;
  layer?: number;
  speedLimitKmh?: number;
  typicalSpeedKmh?: number;
  surfaceType?: SurfaceType;
  lanes?: number;
  widthMeters?: number;
  parentEdgeId?: string;
  isManualAuthority?: boolean;
}

/**
 * DTO for updating a canonical node
 *
 * @remarks
 * All fields are optional - only provided fields will be updated.
 * Primarily used for updating geometry when nodes are moved.
 */
export interface UpdateCanonicalNodeDto {
  geometry?: PointGeometry;
  sourceType?: SourceType;
  sourceReference?: string;
  layer?: number;
  intersectionType?: IntersectionType;
  isManualAuthority?: boolean;
}

/**
 * DTO for updating a canonical edge
 *
 * @remarks
 * All fields are optional - only provided fields will be updated.
 * If geometry is updated, length will be recalculated unless explicitly provided.
 */
export interface UpdateCanonicalEdgeDto {
  geometry?: LineStringGeometry;
  lengthMeters?: number;
  sourceType?: SourceType;
  sourceReference?: string;
  roadClass?: RoadClass;
  accessMode?: AccessMode[];
  oneway?: boolean;
  onewayDirection?: OnewayDirection;
  layer?: number;
  speedLimitKmh?: number;
  typicalSpeedKmh?: number;
  surfaceType?: SurfaceType;
  lanes?: number;
  widthMeters?: number;
  isManualAuthority?: boolean;
  // Campos multinivel (Plan Red Urbana UX - Pasos 8-9)
  streetForm?: StreetForm;
  exclusiveMode?: ExclusiveMode;
  accessRestriction?: AccessRestriction;
  parking?: ParkingConfig;
  condition?: ConditionType;
  smoothness?: SmoothnessType;
  maxHeightM?: number;
  maxWeightT?: number;
  maxWidthM?: number;
}

/**
 * Query parameters for network elements
 */
export interface QueryNetworkParams {
  bounds?: [number, number, number, number]; // [minLng, minLat, maxLng, maxLat]
  status?: 'active' | 'deprecated' | 'all';
  sourceType?: string;
  limit?: number;
}

/**
 * Network statistics
 */
export interface NetworkStats {
  nodeCount: number;
  edgeCount: number;
  totalLengthKm: number;
  intersectionsCount: number;
  byRoadClass: Record<string, number>;
}

// Export de tipos de contexto de edición
export type { NodeContextMenuState } from './contexts/NetworkEditContext';

// ==================== ADMINISTRATIVE BOUNDARIES TYPES ====================

/**
 * Límite administrativo (sin geometría) - Para listados y búsquedas
 */
export interface AdministrativeBoundarySummary {
  id: string;
  code: string;
  name: string;
  fullName: string;
  gid: string;
}

/**
 * Límite administrativo completo (con geometría) - Para selección
 */
export interface AdministrativeBoundary extends AdministrativeBoundarySummary {
  geometry: Polygon | Feature['geometry']; // Polygon o MultiPolygon
}

/**
 * Modo de selección de área de proyecto
 */
export type BoundarySelectionMode = 'rectangle' | 'administrative';

// ==================== BRANCH CREATION TYPES ====================

/**
 * Branch intent type for unified create-branch endpoint
 */
export type BranchIntentType = 'snap-node' | 'free-point' | 'snap-edge';

/**
 * DTO for creating a new node in free-point branch intent
 */
export interface CreateBranchNodeDto {
  geometry: PointGeometry;
  sourceType?: SourceType;
  layer?: number;
  intersectionType?: IntersectionType;
  isManualAuthority?: boolean;
}

/**
 * DTO for edge in branch creation
 */
export interface CreateBranchEdgeDto {
  geometry: LineStringGeometry;
  roadClass: RoadClass;
  sourceType?: SourceType;
  sourceReference?: string;
  oneway?: boolean;
  layer?: number;
}

/**
 * DTO for snap-edge intent (NOT IMPLEMENTED)
 */
export interface SnapEdgeDto {
  edgeId: string;
  snapPoint: PointGeometry;
}

/**
 * Main DTO for create-branch endpoint
 */
export interface CreateBranchDto {
  intentType: BranchIntentType;
  sourceNodeId: string;
  targetNodeId?: string;  // For snap-node
  newNode?: CreateBranchNodeDto;  // For free-point
  snapEdge?: SnapEdgeDto;  // For snap-edge (not implemented)
  edge: CreateBranchEdgeDto;
}

/**
 * Undo snapshot for branch creation
 */
export interface BranchUndoSnapshot {
  createdNodeId?: string;
  createdEdgeIds: string[];
  modifiedEdges?: Array<{
    edgeId: string;
    previousGeometry: LineStringGeometry;
    previousLengthMeters: number;
  }>;
}

/**
 * Result of create-branch operation
 */
export interface CreateBranchResult {
  node: CanonicalNode;
  edges: CanonicalEdge[];
  undoSnapshot: BranchUndoSnapshot;
}

// ==================== EDGE MERGE/UNMERGE TYPES ====================

/**
 * Snapshot of an edge's state before merge (for undo)
 */
export interface EdgeSnapshot {
  id: string;
  fromNodeId: string;
  toNodeId: string;
  geometry: LineStringGeometry;
  attributes: Record<string, unknown>;
}

/**
 * Snapshot of a node's state before merge (for undo)
 */
export interface NodeSnapshot {
  id: string;
  geometry: PointGeometry;
  attributes: Record<string, unknown>;
}

/**
 * Complete snapshot of pre-merge state (for atomic undo)
 */
export interface MergeSnapshot {
  originalEdge1: EdgeSnapshot;
  originalEdge2: EdgeSnapshot;
  sharedNode: NodeSnapshot;
  edge1AssociationIds: string[];
  edge2AssociationIds: string[];
}

/**
 * Response from merge operation
 */
export interface MergeEdgesResponse {
  mergedEdge: {
    id: string;
    fromNodeId: string;
    toNodeId: string;
    geometry: LineStringGeometry;
    lengthMeters: number;
    roadClass: string | null;
  };
  deletedEdgeIds: [string, string];
  deletedNodeId: string;
  migratedAssociationIds: string[];
  snapshot: MergeSnapshot;
}

/**
 * Response from unmerge operation
 */
export interface UnmergeEdgeResponse {
  restoredEdge1: {
    id: string;
    fromNodeId: string;
    toNodeId: string;
    geometry: LineStringGeometry;
  };
  restoredEdge2: {
    id: string;
    fromNodeId: string;
    toNodeId: string;
    geometry: LineStringGeometry;
  };
  restoredNode: {
    id: string;
    geometry: PointGeometry;
  };
}

// ==================== MULTI-MERGE V1 ====================

/**
 * Result of chain validation (Multi-Merge V1)
 */
export interface ChainValidationResult {
  /** Whether the edges form a valid chain */
  isValid: boolean;
  /** Error message if validation failed (null if valid) */
  errorMessage: string | null;
  /** Topologically ordered edge IDs (null if invalid) */
  orderedEdgeIds: string[] | null;
}

/**
 * Snapshot for multi-merge undo (N edges)
 */
export interface MultiMergeSnapshot {
  /** Original edges in topological order */
  originalEdges: EdgeSnapshot[];
  /** Intermediate nodes (degree = 2) that connected the edges */
  intermediateNodes: NodeSnapshot[];
  /** Map of edge ID to array of NetworkPointAssociation IDs */
  associationsByEdge: Record<string, string[]>;
}

/**
 * Response from multi-merge operation
 */
export interface MergeMultipleEdgesResponse {
  mergedEdge: {
    id: string;
    fromNodeId: string;
    toNodeId: string;
    geometry: LineStringGeometry;
    lengthMeters: number | null;
    roadClass: string | null;
  };
  deletedEdgeIds: string[];
  deletedNodeIds: string[];
  migratedAssociationIds: string[];
  snapshot: MultiMergeSnapshot;
}

/**
 * Response from multi-unmerge operation
 */
export interface UnmergeMultipleEdgesResponse {
  restoredEdges: Array<{
    id: string;
    fromNodeId: string;
    toNodeId: string;
    geometry: LineStringGeometry;
  }>;
  restoredNodes: Array<{
    id: string;
    geometry: PointGeometry;
  }>;
}
